import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Importar los componentes de edición específicos
import EditSellerProfile from './edits/EditSellerProfile';
import EditClientProfile from './edits/EditClientProfile';
import EditInmProfile from './edits/EditInmProfile';
import EditConsProfile from './edits/EditConsProfile';

// Mapeo de tipo_usuario_id a tipos de usuario
const USER_TYPE_MAP = {
  1: 'cliente',
  2: 'vendedor',
  3: 'constructora',
  4: 'inmobiliaria'
};

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const determineUserType = async () => {
      try {
        // Verificar si hay token y usuario
        console.log('Token:', !!token);
        console.log('User:', user);

        if (!token || !user) {
          console.log('No token or user, navigating to login');
          navigate('/login');
          return;
        }

        // Determinar el tipo de usuario usando el tipo_usuario_id
        if (user.tipo_usuario_id) {
          const mappedType = USER_TYPE_MAP[user.tipo_usuario_id];
          console.log(`Tipo de usuario según tipo_usuario_id (${user.tipo_usuario_id}):`, mappedType);
          
          if (mappedType) {
            setUserType(mappedType);
            setLoading(false);
            return;
          } else {
            console.warn('tipo_usuario_id no reconocido:', user.tipo_usuario_id);
          }
        } else {
          console.warn('Usuario no tiene tipo_usuario_id');
        }

        // Si no se pudo determinar por tipo_usuario_id, usamos el método alternativo
        console.log('Fallback: Determinando tipo de usuario por propiedades del perfil');
        
        // Hacer una petición al backend para obtener el perfil detallado
        let apiEndpoint = 'https://backend-production-1786.up.railway.app/api/vendedores/perfil';
        
        // Si sospechamos que es una constructora, intentamos con ese endpoint primero
        if (user.nombre_empresa || user.ruc) {
          try {
            const constructoraResponse = await fetch('https://backend-production-1786.up.railway.app/api/constructoras/perfil-detallado', {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (constructoraResponse.ok) {
              console.log('Perfil de constructora encontrado');
              setUserType('constructora');
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('No es una constructora, intentando con otros perfiles');
          }
        }
        
        // Petición estándar para otros tipos de perfiles
        const response = await fetch(apiEndpoint, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Fetch response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response text:', errorText);
          throw new Error(`Error al obtener el perfil: ${response.status} ${errorText}`);
        }

        const data = await response.json();
        console.log('Perfil data:', data);

        // Determinar el tipo de usuario basado en los datos del perfil
        let tipoUsuario;
        
        // Verificar si es una inmobiliaria (tiene RUC pero no horario de atención)
        if (data.ruc && !data.horario_atencion) {
          tipoUsuario = 'inmobiliaria';
        }
        // Verificar si es un cliente (tiene preferencias)
        else if (data.preferencias) {
          tipoUsuario = 'cliente';
        }
        // Por defecto, es un vendedor
        else {
          tipoUsuario = 'vendedor';
        }

        console.log('Tipo de usuario determinado por propiedades:', tipoUsuario);
        setUserType(tipoUsuario);
      } catch (error) {
        console.error('Error completo al verificar el tipo de usuario:', error);
        setError(error.message);
        // En caso de error, mostrar el error en lugar de redirigir inmediatamente
      } finally {
        setLoading(false);
      }
    };

    determineUserType();
  }, [token, user, navigate]);

  // Mostrar un indicador de carga más atractivo
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tu perfil...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-gray-800">Ocurrió un error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => {
              localStorage.removeItem('token');
              navigate('/login');
            }}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors shadow-md"
          >
            Volver al Inicio de Sesión
          </button>
        </div>
      </div>
    );
  }

  // Renderizado condicional basado en el tipo de usuario
  switch (userType) {
    case 'vendedor':
      return <EditSellerProfile />;
    case 'cliente':
      return <EditClientProfile />;
    case 'inmobiliaria':
      return <EditInmProfile />;
    case 'constructora':
      return <EditConsProfile />;
    default:
      // Caso de tipo de usuario desconocido - UI mejorada
      return (
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2 text-gray-800">Tipo de Usuario No Reconocido</h2>
            <p className="text-gray-600 mb-6">No pudimos determinar el tipo de perfil para mostrar el editor adecuado.</p>
            <div className="flex gap-4 justify-center">
              <button 
                onClick={() => navigate('/mi_perfil')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors shadow-md"
              >
                Ver mi Perfil
              </button>
              <button 
                onClick={() => {
                  localStorage.removeItem('token');
                  navigate('/login');
                }}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors shadow-md"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      );
  }
};

export default EditProfile;